import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { 
  Menu, X, Phone, Mail, MapPin, ChevronRight, 
  Users, Heart, Briefcase, GraduationCap, MessageCircle, BookOpen,
  Quote, ArrowRight, ExternalLink, Scale, Building,
  CheckCircle, Sparkles, RotateCcw, Clock, Target, Lightbulb,
  Calculator, Bot, Send, TrendingDown, Coins, Timer, ShieldCheck
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Users: Users,
  Heart: Heart,
  Briefcase: Briefcase,
  GraduationCap: GraduationCap,
  MessageCircle: MessageCircle,
  BookOpen: BookOpen,
  Scale: Scale,
  Building: Building,
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Triage", path: "/triage" },
    { name: "Estimator", path: "/estimator" },
    { name: "About", path: "/about" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="/logo-60.png" 
              alt="Sermo Logo" 
              className="w-10 h-10 rounded-lg"
              data-testid="nav-logo-image"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold text-sky-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sermo
              </span>
              <span className="text-xs text-slate-400 hidden sm:inline leading-tight">The Art of Conversation</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors link-underline ${
                  location.pathname === link.path
                    ? "text-sky-400"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact">
              <Button 
                data-testid="nav-cta-button"
                className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-6"
              >
                Get in Touch
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-slate-300 hover:text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden mt-4 pb-4 border-t border-slate-800 animate-slideDown"
          >
            <div className="flex flex-col space-y-4 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`text-base font-medium ${
                    location.pathname === link.path
                      ? "text-sky-400"
                      : "text-slate-300"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button 
                  data-testid="mobile-cta-button"
                  className="w-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold rounded-full"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-4 mb-4">
              <img 
                src="/logo-120.png" 
                alt="Sermo Logo" 
                className="w-16 h-16 rounded-lg"
                data-testid="footer-logo-image"
              />
              <h3 className="text-2xl font-bold text-sky-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sermo
              </h3>
            </div>
            <p className="text-slate-400 mb-6 max-w-md">
              From the Latin word for conversation and discourse. We believe that through dialogue, 
              conflicts can be resolved and relationships rebuilt.
            </p>
            <div className="flex items-center space-x-4 text-slate-400">
              <Phone size={18} className="text-sky-400" />
              <a href="tel:07768683593" className="hover:text-white transition-colors" data-testid="footer-phone">
                07768 683593
              </a>
            </div>
            <div className="flex items-center space-x-4 text-slate-400 mt-2">
              <Mail size={18} className="text-sky-400" />
              <a href="mailto:enquiries@sermo.uk" className="hover:text-white transition-colors" data-testid="footer-email">
                enquiries@sermo.uk
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {["Services", "About", "FAQ", "Contact"].map((item) => (
                <li key={item}>
                  <Link
                    to={`/${item.toLowerCase()}`}
                    className="text-slate-400 hover:text-sky-400 transition-colors flex items-center"
                    data-testid={`footer-link-${item.toLowerCase()}`}
                  >
                    <ChevronRight size={16} className="mr-1" />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Our Services</h4>
            <ul className="space-y-3">
              {[
                { name: "Community", label: "Community Mediation" },
                { name: "Probate", label: "Probate Mediation" },
                { name: "Workplace", label: "Workplace Mediation" },
                { name: "Civil", label: "Civil & Commercial" }
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to="/services"
                    className="text-slate-400 hover:text-sky-400 transition-colors flex items-center"
                    data-testid={`footer-service-${item.name.toLowerCase()}`}
                  >
                    <ChevronRight size={16} className="mr-1" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Sermo. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm mt-4 md:mt-0">
            Contact Tracey: <a href="tel:07768683593" className="text-sky-400 hover:text-sky-300">07768 683593</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`)
        ]);
        setServices(servicesRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center gradient-radial noise">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1768013250875-b1ed7c2dfc52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNvbm5lY3Rpb24lMjBibHVlJTIwY2FsbSUyMHVudGFuZ2xpbmclMjBrbm90c3xlbnwwfHx8fDE3NzI4ODM4NjJ8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <div className="order-2 lg:order-1">
              <p className="text-sky-400 font-medium mb-4 animate-fadeIn tracking-wider uppercase text-sm">
                Professional Mediation Services
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-slideUp" style={{ fontFamily: 'Playfair Display, serif' }}>
                Resolving Conflict Through{" "}
                <span className="text-sky-400">Conversation</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 animate-slideUp stagger-1 max-w-2xl">
                Sermo — from the Latin word for discourse. We believe that meaningful dialogue 
                can transform conflicts into understanding, rebuilding relationships one conversation at a time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-slideUp stagger-2">
              <Link to="/contact">
                <Button 
                  data-testid="hero-cta-primary"
                  className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-8 py-6 text-base"
                >
                  Start the Conversation
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  data-testid="hero-cta-secondary"
                  variant="outline" 
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full px-8 py-6 text-base"
                >
                  Explore Our Services
                </Button>
              </Link>
              </div>
            </div>
            
            {/* Right side - Logo */}
            <div className="order-1 lg:order-2 flex justify-center lg:justify-end animate-scaleIn">
              <div className="relative">
                <div className="absolute -inset-4 bg-sky-500/20 rounded-full blur-3xl"></div>
                <img 
                  src="/logo-400.png" 
                  alt="Sermo - The Art of Conversation" 
                  className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-2xl shadow-2xl object-cover"
                  data-testid="hero-logo-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 relative gradient-radial-bottom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Where Do You Need Help?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We offer specialized mediation services across different areas of life. 
              Each conflict is unique, and we tailor our approach to meet your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Users;
              return (
                <Card
                  key={service.id}
                  data-testid={`service-card-${service.slug}`}
                  className={`bg-slate-900/50 border-slate-800 card-hover animate-slideUp stagger-${index + 1}`}
                >
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-sky-500/10 flex items-center justify-center mb-4">
                      <IconComponent className="text-sky-400" size={24} />
                    </div>
                    <CardTitle className="text-white text-xl">{service.name}</CardTitle>
                    <CardDescription className="text-slate-400">
                      {service.short_description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link 
                      to="/services" 
                      className="text-sky-400 hover:text-sky-300 flex items-center text-sm font-medium"
                    >
                      Learn more <ChevronRight size={16} className="ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Sermo */}
      <section className="py-24 md:py-32 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Why Choose Sermo?
              </h2>
              <p className="text-slate-300 mb-8 text-lg">
                Our success lies in our ability to resolve conflict and rebuild relationships. 
                We provide an independent and impartial service to those experiencing conflict.
              </p>
              <div className="space-y-6">
                {[
                  { title: "Experienced Mediators", desc: "Our trained professionals bring diverse backgrounds and expertise." },
                  { title: "Confidential Process", desc: "Everything discussed in mediation remains completely private." },
                  { title: "Cost-Effective", desc: "Significantly more affordable than legal proceedings." },
                  { title: "Faster Resolution", desc: "Most disputes can be resolved in just 2-3 sessions." },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-sky-400"></div>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759593047536-5258c3c6a527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHx0d28lMjBwZW9wbGUlMjB0YWxraW5nJTIwZnJpZW5kbHl8ZW58MHx8fHwxNzcyODgzODcxfDA&ixlib=rb-4.1.0&q=85"
                alt="People in conversation"
                className="rounded-lg shadow-2xl"
                data-testid="why-sermo-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900 border border-slate-800 rounded-lg p-6 shadow-xl">
                <p className="text-3xl font-bold text-sky-400">98%</p>
                <p className="text-slate-400 text-sm">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Our Clients Say
            </h2>
          </div>

          {testimonials.length > 0 && (
            <div 
              data-testid="testimonial-carousel"
              className="relative bg-slate-900/50 border border-slate-800 rounded-2xl p-8 md:p-12"
            >
              <Quote className="text-sky-400/20 absolute top-6 left-6" size={48} />
              <div className="relative z-10">
                <blockquote className="text-xl md:text-2xl text-slate-200 mb-6 italic text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                  "{testimonials[currentTestimonial]?.quote}"
                </blockquote>
                <div className="text-center">
                  <p className="text-white font-semibold">{testimonials[currentTestimonial]?.author}</p>
                  <p className="text-sky-400 text-sm">{testimonials[currentTestimonial]?.role}</p>
                </div>
              </div>
              
              {/* Dots */}
              <div className="flex justify-center space-x-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    data-testid={`testimonial-dot-${index}`}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentTestimonial ? "bg-sky-400" : "bg-slate-600"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Triage CTA */}
      <section className="py-16 md:py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card 
            data-testid="home-triage-cta"
            className="bg-gradient-to-br from-sky-500/10 to-slate-900/50 border-sky-500/20"
          >
            <CardContent className="p-8 md:p-12 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sky-500/20 mb-6">
                <Sparkles className="text-sky-400" size={28} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Not Sure Where to Start?
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                Answer a few quick questions and our Triage Assistant will suggest 
                how our mediation services can help your specific situation.
              </p>
              <Link to="/triage">
                <Button 
                  data-testid="home-triage-button"
                  className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-8 py-6 text-base"
                >
                  Start Triage Assistant
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative gradient-radial">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Start the Conversation?
          </h2>
          <p className="text-slate-300 mb-8 text-lg max-w-2xl mx-auto">
            Taking the first step is often the hardest. Contact Tracey today for a 
            confidential discussion about how we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07768683593">
              <Button 
                data-testid="cta-phone"
                className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-8 py-6 text-base"
              >
                <Phone className="mr-2" size={18} />
                Call 07768 683593
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                data-testid="cta-contact"
                variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full px-8 py-6 text-base"
              >
                <Mail className="mr-2" size={18} />
                Send a Message
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Services Page
const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page" className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our <span className="text-sky-400">Services</span>
            </h1>
            <p className="text-lg text-slate-300">
              We offer a comprehensive range of mediation services to help resolve conflicts 
              in various settings. Each service is tailored to address the unique dynamics 
              of different types of disputes.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Users;
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={service.id}
                  data-testid={`service-detail-${service.slug}`}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  <div className={isEven ? "" : "lg:order-2"}>
                    <div className="w-16 h-16 rounded-xl bg-sky-500/10 flex items-center justify-center mb-6">
                      <IconComponent className="text-sky-400" size={32} />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {service.name}
                    </h2>
                    <p className="text-slate-300 mb-6 text-lg">
                      {service.full_description}
                    </p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Key Benefits:</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start text-slate-400">
                            <div className="w-5 h-5 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-sky-400"></div>
                            </div>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to="/contact" className="mt-6 inline-block">
                      <Button 
                        data-testid={`service-cta-${service.slug}`}
                        className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-6 mt-6"
                      >
                        Enquire About {service.name}
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                  <div className={`bg-slate-900/50 border border-slate-800 rounded-2xl p-8 ${isEven ? "" : "lg:order-1"}`}>
                    <div className="aspect-video rounded-lg bg-slate-800/50 flex items-center justify-center">
                      <IconComponent className="text-sky-400/30" size={80} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Mediation Process
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Understanding what to expect can help ease any concerns about mediation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Initial Contact", desc: "Reach out to us to discuss your situation confidentially." },
              { step: 2, title: "Preparation", desc: "We meet with each party separately to understand perspectives." },
              { step: 3, title: "Mediation Session", desc: "Facilitated dialogue to explore solutions together." },
              { step: 4, title: "Agreement", desc: "Document agreed outcomes and next steps." },
            ].map((item) => (
              <div key={item.step} className="relative" data-testid={`process-step-${item.step}`}>
                <div className="w-12 h-12 rounded-full bg-sky-500 flex items-center justify-center text-slate-900 font-bold text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
                {item.step < 4 && (
                  <div className="hidden md:block absolute top-6 left-16 w-full h-0.5 bg-slate-800">
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                      <ChevronRight className="text-slate-600" size={16} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div data-testid="about-page" className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img 
              src="/logo-120.png" 
              alt="Sermo Logo" 
              className="w-24 h-24 rounded-xl shadow-lg"
              data-testid="about-header-logo"
            />
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                About <span className="text-sky-400">Sermo</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl">
                Sermo — the Latin word for conversation, discourse, and dialogue. 
                We believe that through meaningful communication, any conflict can find resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Mission
              </h2>
              <p className="text-slate-300 mb-6">
                At Sermo, our success lies in our ability to resolve conflict and rebuild relationships. 
                We are dedicated to providing an independent and impartial lifeline to those experiencing 
                conflict in the community, within a family, in a school, or in a workplace environment.
              </p>
              <p className="text-slate-300 mb-6">
                We help people find their voice, understand each other's perspectives, and work together 
                towards solutions that work for everyone involved. Our approach is rooted in the belief 
                that most conflicts arise from miscommunication and misunderstanding — and that these 
                can be overcome through structured, facilitated dialogue.
              </p>
              <blockquote className="border-l-4 border-sky-400 pl-6 italic text-slate-400">
                "It always seems impossible until it's done."
              </blockquote>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1624555130296-e551faf8969b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwdGVhbSUyMG1lZXRpbmclMjBmcmllbmRseXxlbnwwfHx8fDE3NzI4ODM4NjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional team meeting"
                className="rounded-lg shadow-2xl"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Impartiality",
                desc: "We don't take sides. Our role is to facilitate understanding, not to judge or decide who is right.",
                icon: "⚖️"
              },
              {
                title: "Confidentiality",
                desc: "What happens in mediation stays in mediation. This creates a safe space for honest dialogue.",
                icon: "🔒"
              },
              {
                title: "Empowerment",
                desc: "We help you find your own solutions. You remain in control of the outcome throughout the process.",
                icon: "💪"
              },
            ].map((value, i) => (
              <Card 
                key={i} 
                data-testid={`value-card-${i}`}
                className="bg-slate-900/50 border-slate-800 card-hover"
              >
                <CardHeader>
                  <div className="text-4xl mb-4">{value.icon}</div>
                  <CardTitle className="text-white text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Meet Tracey
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Your first point of contact and guide through the mediation process.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-slate-900/50 border-slate-800" data-testid="team-card">
              <CardContent className="p-8 text-center">
                <div className="w-32 h-32 rounded-full bg-sky-500/20 mx-auto mb-6 flex items-center justify-center">
                  <span className="text-4xl text-sky-400" style={{ fontFamily: 'Playfair Display, serif' }}>T</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Tracey</h3>
                <p className="text-sky-400 mb-4">Lead Mediator & Founder</p>
                <p className="text-slate-400 mb-6">
                  With years of experience in conflict resolution, Tracey founded Sermo to help 
                  people find their way back to understanding through the power of conversation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:07768683593">
                    <Button 
                      data-testid="team-phone-button"
                      className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full"
                    >
                      <Phone className="mr-2" size={16} />
                      07768 683593
                    </Button>
                  </a>
                  <a href="mailto:enquiries@sermo.uk">
                    <Button 
                      data-testid="team-email-button"
                      variant="outline" 
                      className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full"
                    >
                      <Mail className="mr-2" size={16} />
                      Email Tracey
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

// FAQ Page
const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await axios.get(`${API}/faqs`);
        setFaqs(res.data);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <div data-testid="faq-page" className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked <span className="text-sky-400">Questions</span>
            </h1>
            <p className="text-lg text-slate-300">
              Find answers to common questions about mediation and our services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                data-testid={`faq-item-${index}`}
                className="bg-slate-900/50 border border-slate-800 rounded-lg px-6 data-[state=open]:border-sky-500/50"
              >
                <AccordionTrigger className="text-white hover:text-sky-400 text-left py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-6">
              Still have questions? We're here to help.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="faq-contact-button"
                className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-8"
              >
                Get in Touch
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API}/contact`, formData);
      if (res.data.status === "success") {
        toast.success("Message sent successfully! We'll be in touch soon.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          service_type: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Get in <span className="text-sky-400">Touch</span>
            </h1>
            <p className="text-lg text-slate-300">
              Taking the first step is often the hardest. Reach out today for a confidential 
              discussion about how we can help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Send a Message</CardTitle>
                  <CardDescription className="text-slate-400">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300">Name *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name-input"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="bg-slate-950 border-slate-800 text-white focus:border-sky-500"
                          placeholder="Your name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="bg-slate-950 border-slate-800 text-white focus:border-sky-500"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone-input"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="bg-slate-950 border-slate-800 text-white focus:border-sky-500"
                          placeholder="Your phone number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-slate-300">Service Interest</Label>
                        <Select
                          value={formData.service_type}
                          onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                        >
                          <SelectTrigger 
                            data-testid="contact-service-select"
                            className="bg-slate-950 border-slate-800 text-white focus:border-sky-500"
                          >
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="community" className="text-white">Community Mediation</SelectItem>
                            <SelectItem value="family" className="text-white">Family Mediation</SelectItem>
                            <SelectItem value="workplace" className="text-white">Workplace Mediation</SelectItem>
                            <SelectItem value="schools" className="text-white">Schools Mediation</SelectItem>
                            <SelectItem value="coaching" className="text-white">Conflict Coaching</SelectItem>
                            <SelectItem value="training" className="text-white">Training</SelectItem>
                            <SelectItem value="general" className="text-white">General Enquiry</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">Message *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        className="bg-slate-950 border-slate-800 text-white focus:border-sky-500 min-h-[150px]"
                        placeholder="Tell us about your situation..."
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={isSubmitting}
                      className="w-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full py-6"
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Contact Information
                </h2>
                <p className="text-slate-400 mb-8">
                  Prefer to speak directly? Give Tracey a call for a confidential discussion 
                  about your situation.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="bg-slate-900/50 border-slate-800 card-hover" data-testid="contact-info-phone">
                  <CardContent className="p-6 flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center">
                      <Phone className="text-sky-400" size={24} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Phone</p>
                      <a href="tel:07768683593" className="text-white font-semibold text-lg hover:text-sky-400 transition-colors">
                        07768 683593
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 card-hover" data-testid="contact-info-email">
                  <CardContent className="p-6 flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center">
                      <Mail className="text-sky-400" size={24} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Email</p>
                      <a href="mailto:enquiries@sermo.uk" className="text-white font-semibold text-lg hover:text-sky-400 transition-colors">
                        enquiries@sermo.uk
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 card-hover" data-testid="contact-info-person">
                  <CardContent className="p-6 flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center">
                      <MessageCircle className="text-sky-400" size={24} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Your Contact</p>
                      <p className="text-white font-semibold text-lg">Tracey</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mt-8">
                <Quote className="text-sky-400/20 mb-4" size={32} />
                <p className="text-slate-300 italic mb-4">
                  "Do not find fault, find a remedy."
                </p>
                <p className="text-slate-500 text-sm">— Henry Ford</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Triage Assistant Page
const TriagePage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const questions = [
    {
      id: "conflictType",
      question: "What type of conflict are you experiencing?",
      icon: Users,
      description: "Choose the option that best describes your situation.",
      options: [
        { value: "community", label: "Neighbour or Community Dispute", desc: "Noise, boundaries, parking, or community issues" },
        { value: "probate", label: "Probate or Inheritance", desc: "Will disputes, estate distribution, or executor issues" },
        { value: "workplace", label: "Workplace Conflict", desc: "Colleague disagreements, management issues, or team conflicts" },
        { value: "civil-commercial", label: "Civil or Commercial", desc: "Business disputes, contracts, or partnership issues" },
        { value: "other", label: "Something Else", desc: "A different type of dispute or unsure" },
      ]
    },
    {
      id: "stage",
      question: "What stage is your dispute at?",
      icon: Clock,
      description: "This helps us understand the urgency and best approach.",
      options: [
        { value: "early", label: "Just Beginning", desc: "Tensions are rising but manageable" },
        { value: "ongoing", label: "Ongoing for Some Time", desc: "The situation has been unresolved for weeks or months" },
        { value: "escalated", label: "Significantly Escalated", desc: "Communication has broken down; parties are entrenched" },
        { value: "legal", label: "Legal Action Considered", desc: "Solicitors involved or court proceedings imminent" },
      ]
    },
    {
      id: "attempts",
      question: "Have you tried to resolve it already?",
      icon: RotateCcw,
      description: "Understanding previous attempts helps us tailor our approach.",
      options: [
        { value: "none", label: "No, Not Yet", desc: "Looking for guidance on where to start" },
        { value: "direct", label: "Tried Talking Directly", desc: "We've had conversations but no resolution" },
        { value: "third-party", label: "Involved Others", desc: "Family, friends, or authorities have tried to help" },
        { value: "professional", label: "Professional Help", desc: "Have engaged solicitors or other professionals" },
      ]
    },
    {
      id: "outcome",
      question: "What outcome are you hoping for?",
      icon: Target,
      description: "Understanding your goals helps us guide you effectively.",
      options: [
        { value: "restore", label: "Restore the Relationship", desc: "Rebuild trust and continue the connection" },
        { value: "resolve", label: "Reach a Fair Agreement", desc: "Find a workable solution both parties can accept" },
        { value: "closure", label: "Get Closure and Move On", desc: "Bring the matter to a definitive end" },
        { value: "understand", label: "Understand My Options", desc: "Not sure yet - need advice on possibilities" },
      ]
    },
    {
      id: "urgency",
      question: "How soon do you need help?",
      icon: Sparkles,
      description: "We'll prioritise urgent cases where possible.",
      options: [
        { value: "urgent", label: "As Soon As Possible", desc: "The situation is critical and time-sensitive" },
        { value: "weeks", label: "Within a Few Weeks", desc: "I'd like to address this soon but have some flexibility" },
        { value: "flexible", label: "I'm Just Exploring", desc: "Learning about my options without immediate pressure" },
      ]
    }
  ];

  const getRecommendation = () => {
    const serviceMap = {
      community: {
        name: "Community Mediation",
        slug: "community",
        icon: Users,
        summary: "Our Community Mediation service is designed exactly for situations like yours. We'll help facilitate honest conversation between all parties in a safe, structured environment."
      },
      probate: {
        name: "Probate Mediation",
        slug: "probate",
        icon: Scale,
        summary: "Our Probate Mediation service specialises in inheritance and estate disputes. We approach these emotionally sensitive matters with care, helping preserve family relationships while reaching fair agreements."
      },
      workplace: {
        name: "Workplace Mediation",
        slug: "workplace",
        icon: Briefcase,
        summary: "Our Workplace Mediation service resolves conflicts before they escalate into formal grievances. We work confidentially with all parties to restore productive working relationships."
      },
      "civil-commercial": {
        name: "Civil & Commercial Mediation",
        slug: "civil-commercial",
        icon: Building,
        summary: "Our Civil & Commercial Mediation service offers a cost-effective alternative to litigation for business and civil disputes. We help preserve valuable relationships while reaching fair commercial outcomes."
      },
      other: {
        name: "Personal Consultation",
        slug: "contact",
        icon: MessageCircle,
        summary: "Every conflict is unique. Contact Tracey for a confidential conversation about your situation, and we'll help identify the best path forward for you."
      }
    };

    const stageMessages = {
      early: "Reaching out early is smart — mediation is most effective before positions become entrenched.",
      ongoing: "Even long-running disputes can be resolved through mediation. The willingness to engage is what matters most.",
      escalated: "Highly escalated situations often benefit most from mediation. A neutral third party can break through communication barriers.",
      legal: "Mediation can run in parallel with legal proceedings and often resolves matters before court, saving significant time and cost."
    };

    const urgencyMessages = {
      urgent: "We understand the urgency. Call Tracey directly on 07768 683593 for the fastest response.",
      weeks: "We can typically arrange an initial consultation within 1-2 weeks.",
      flexible: "Take your time. We're here whenever you're ready to explore your options."
    };

    return {
      service: serviceMap[answers.conflictType] || serviceMap.other,
      stageMessage: stageMessages[answers.stage] || "",
      urgencyMessage: urgencyMessages[answers.urgency] || "",
    };
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [questions[currentStep].id]: value };
    setAnswers(newAnswers);
    
    if (currentStep < questions.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 200);
    } else {
      setTimeout(() => setShowResult(true), 300);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = ((currentStep + (showResult ? 1 : 0)) / questions.length) * 100;

  if (showResult) {
    const rec = getRecommendation();
    const IconComponent = rec.service.icon;
    
    return (
      <div data-testid="triage-result-page" className="pt-24 min-h-screen">
        <section className="py-16 md:py-24 relative gradient-radial">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Result Header */}
            <div className="text-center mb-12 animate-slideUp">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-500/10 mb-6">
                <CheckCircle className="text-sky-400" size={32} />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your <span className="text-sky-400">Recommendation</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                Based on your answers, here's how we can help.
              </p>
            </div>

            {/* Recommended Service Card */}
            <Card 
              data-testid="triage-recommendation-card"
              className="bg-slate-900/50 border-slate-800 mb-8 animate-slideUp stagger-1"
            >
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="text-sky-400" size={28} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sky-400 text-sm font-medium mb-1 uppercase tracking-wider">Recommended Service</p>
                    <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {rec.service.name}
                    </h2>
                    <p className="text-slate-300 leading-relaxed">
                      {rec.service.summary}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional insights */}
            {rec.stageMessage && (
              <Card className="bg-slate-900/30 border-slate-800/50 mb-4 animate-slideUp stagger-2">
                <CardContent className="p-6 flex items-start space-x-4">
                  <Lightbulb className="text-sky-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-slate-300">{rec.stageMessage}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {rec.urgencyMessage && (
              <Card className="bg-slate-900/30 border-slate-800/50 mb-8 animate-slideUp stagger-3">
                <CardContent className="p-6 flex items-start space-x-4">
                  <Clock className="text-sky-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-slate-300">{rec.urgencyMessage}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next Steps */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 mb-8 animate-slideUp stagger-4">
              <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Next Steps
              </h3>
              <div className="space-y-4 mb-8">
                {[
                  { num: "1", title: "Get in Touch", desc: "Contact us via phone or the contact form to discuss your situation confidentially." },
                  { num: "2", title: "Initial Conversation", desc: "We'll listen to your story and explain how mediation could work for you." },
                  { num: "3", title: "Begin the Process", desc: "If mediation feels right, we'll arrange sessions at a pace that suits you." },
                ].map((step) => (
                  <div key={step.num} className="flex items-start space-x-4" data-testid={`next-step-${step.num}`}>
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-slate-900 font-bold flex-shrink-0 text-sm">
                      {step.num}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{step.title}</h4>
                      <p className="text-slate-400 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:07768683593" className="flex-1">
                  <Button 
                    data-testid="triage-cta-call"
                    className="w-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full py-6"
                  >
                    <Phone className="mr-2" size={18} />
                    Call Tracey: 07768 683593
                  </Button>
                </a>
                <Link to="/contact" className="flex-1">
                  <Button 
                    data-testid="triage-cta-contact"
                    variant="outline" 
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full py-6"
                  >
                    <Mail className="mr-2" size={18} />
                    Send a Message
                  </Button>
                </Link>
              </div>
            </div>

            {/* Restart */}
            <div className="text-center">
              <button
                data-testid="triage-restart-button"
                onClick={handleReset}
                className="text-slate-400 hover:text-sky-400 transition-colors inline-flex items-center text-sm"
              >
                <RotateCcw size={14} className="mr-2" />
                Start Over
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];
  const CurrentIcon = currentQuestion.icon;

  return (
    <div data-testid="triage-page" className="pt-24 min-h-screen">
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sky-400 font-medium mb-3 tracking-wider uppercase text-sm">
              Triage Assistant
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Find Your <span className="text-sky-400">Path Forward</span>
            </h1>
            <p className="text-slate-400 max-w-xl mx-auto">
              Answer a few quick questions and we'll suggest how our mediation services could help you.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8" data-testid="triage-progress">
            <div className="flex justify-between text-sm text-slate-400 mb-2">
              <span>Question {currentStep + 1} of {questions.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-sky-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <Card 
            key={currentStep}
            data-testid={`triage-question-${currentStep}`}
            className="bg-slate-900/50 border-slate-800 animate-scaleIn"
          >
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <CurrentIcon className="text-sky-400" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {currentQuestion.question}
                  </h2>
                  <p className="text-slate-400 text-sm">{currentQuestion.description}</p>
                </div>
              </div>

              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={option.value}
                    data-testid={`triage-option-${currentStep}-${option.value}`}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full text-left p-4 rounded-lg border transition-all duration-200 group ${
                      answers[currentQuestion.id] === option.value
                        ? "bg-sky-500/10 border-sky-500/50"
                        : "bg-slate-950/50 border-slate-800 hover:border-sky-500/30 hover:bg-slate-900/50"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-white font-medium mb-1 group-hover:text-sky-400 transition-colors">
                          {option.label}
                        </p>
                        <p className="text-slate-400 text-sm">{option.desc}</p>
                      </div>
                      <ChevronRight 
                        className="text-slate-600 group-hover:text-sky-400 transition-colors flex-shrink-0 ml-4 mt-1" 
                        size={20} 
                      />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          {currentStep > 0 && (
            <div className="mt-6 flex justify-between items-center">
              <button
                data-testid="triage-back-button"
                onClick={handleBack}
                className="text-slate-400 hover:text-sky-400 transition-colors inline-flex items-center text-sm"
              >
                <ChevronRight size={16} className="mr-1 rotate-180" />
                Back
              </button>
              <button
                data-testid="triage-restart-button-nav"
                onClick={handleReset}
                className="text-slate-500 hover:text-sky-400 transition-colors inline-flex items-center text-xs"
              >
                <RotateCcw size={12} className="mr-1" />
                Start Over
              </button>
            </div>
          )}

          {/* Reassurance */}
          <div className="text-center mt-12 text-slate-500 text-sm">
            <p>Your answers are private and not stored. This is just a guide.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================
// Cost & Time Savings Estimator
// ============================================
const CostEstimatorPage = () => {
  const [formData, setFormData] = useState({
    dispute_type: "",
    dispute_value: "",
    complexity: "medium"
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    if (!formData.dispute_type) {
      toast.error("Please select a dispute type");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/cost-estimate`, {
        dispute_type: formData.dispute_type,
        dispute_value: formData.dispute_value ? parseFloat(formData.dispute_value) : null,
        complexity: formData.complexity
      });
      setResult(res.data);
      setTimeout(() => {
        document.getElementById("estimator-results")?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } catch (error) {
      toast.error("Failed to calculate estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({ dispute_type: "", dispute_value: "", complexity: "medium" });
    setResult(null);
  };

  const formatCurrency = (num) => `£${num.toLocaleString('en-GB')}`;

  return (
    <div data-testid="estimator-page" className="pt-24 min-h-screen">
      {/* Header */}
      <section className="py-16 md:py-24 relative gradient-radial">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-500/10 mb-6">
              <Calculator className="text-sky-400" size={32} />
            </div>
            <p className="text-sky-400 font-medium mb-3 tracking-[0.2em] uppercase text-xs">
              — Cost & Time Estimator —
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Court vs <span className="text-sky-400 italic">Mediation</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Discover how much time and money mediation can save you compared to traditional court proceedings.
              Based on UK average costs and a standard £1,500 daily mediation rate.
            </p>
          </motion.div>

          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="bg-slate-900/50 border-slate-800 backdrop-blur-sm">
              <CardContent className="p-8 md:p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">Type of Dispute</Label>
                    <Select
                      value={formData.dispute_type}
                      onValueChange={(value) => setFormData({ ...formData, dispute_type: value })}
                    >
                      <SelectTrigger 
                        data-testid="estimator-dispute-type"
                        className="bg-slate-950 border-slate-800 text-white h-12"
                      >
                        <SelectValue placeholder="Select dispute type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="community" className="text-white">Community / Neighbour</SelectItem>
                        <SelectItem value="probate" className="text-white">Probate / Inheritance</SelectItem>
                        <SelectItem value="workplace" className="text-white">Workplace</SelectItem>
                        <SelectItem value="civil-commercial" className="text-white">Civil & Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">Complexity</Label>
                    <Select
                      value={formData.complexity}
                      onValueChange={(value) => setFormData({ ...formData, complexity: value })}
                    >
                      <SelectTrigger 
                        data-testid="estimator-complexity"
                        className="bg-slate-950 border-slate-800 text-white h-12"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="simple" className="text-white">Simple - Clear-cut issue</SelectItem>
                        <SelectItem value="medium" className="text-white">Medium - Some complexity</SelectItem>
                        <SelectItem value="complex" className="text-white">Complex - Multiple parties/issues</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(formData.dispute_type === "probate" || formData.dispute_type === "civil-commercial") && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mb-6"
                  >
                    <Label className="text-slate-300 text-sm mb-2 block">
                      Approximate Value of Dispute (£) <span className="text-slate-500 text-xs">Optional</span>
                    </Label>
                    <Input
                      data-testid="estimator-value"
                      type="number"
                      value={formData.dispute_value}
                      onChange={(e) => setFormData({ ...formData, dispute_value: e.target.value })}
                      placeholder="e.g. 50000"
                      className="bg-slate-950 border-slate-800 text-white h-12"
                    />
                  </motion.div>
                )}

                <Button
                  data-testid="estimator-calculate"
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full py-6 text-base"
                >
                  {loading ? "Calculating..." : "Calculate Savings"}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <AnimatePresence>
            {result && (
              <motion.div
                id="estimator-results"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="mt-12"
              >
                {/* Savings Highlight */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-br from-sky-500/20 via-slate-900/80 to-slate-950 border border-sky-500/30 rounded-2xl p-8 md:p-12 mb-8 text-center relative overflow-hidden"
                  data-testid="estimator-results-summary"
                >
                  <div className="absolute inset-0 opacity-30" style={{
                    backgroundImage: `radial-gradient(circle at 20% 50%, rgba(14, 165, 233, 0.3) 0%, transparent 50%),
                                     radial-gradient(circle at 80% 50%, rgba(56, 189, 248, 0.2) 0%, transparent 50%)`
                  }}></div>
                  <div className="relative z-10">
                    <p className="text-sky-400 font-medium tracking-[0.2em] uppercase text-xs mb-4">
                      — Your Potential Savings —
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4, duration: 0.8 }}
                          className="text-5xl md:text-6xl font-bold text-white mb-2"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          {formatCurrency(result.savings.amount)}
                        </motion.p>
                        <p className="text-slate-400 text-sm">
                          {result.savings.percent}% cost saving
                        </p>
                      </div>
                      <div>
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6, duration: 0.8 }}
                          className="text-5xl md:text-6xl font-bold text-white mb-2"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          {result.savings.time_display}
                        </motion.p>
                        <p className="text-slate-400 text-sm">
                          time saved
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Court Card */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card 
                      data-testid="estimator-court-card"
                      className="bg-slate-900/50 border-red-900/30 h-full"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                            Court
                          </h3>
                          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                            <Scale className="text-red-400" size={20} />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Coins size={16} className="mr-2" /> Estimated Cost
                            </span>
                            <span className="text-white font-semibold text-lg">{formatCurrency(result.court.cost)}</span>
                          </div>
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Timer size={16} className="mr-2" /> Duration
                            </span>
                            <span className="text-white font-semibold">{result.court.duration_display}</span>
                          </div>
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <TrendingDown size={16} className="mr-2" /> Stress Level
                            </span>
                            <span className="text-red-400 font-semibold text-sm">{result.court.stress_level}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Target size={16} className="mr-2" /> Control
                            </span>
                            <span className="text-slate-300 text-sm">{result.court.outcome_control}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Mediation Card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card 
                      data-testid="estimator-mediation-card"
                      className="bg-gradient-to-br from-sky-500/10 to-slate-900/50 border-sky-500/50 h-full"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                            Mediation
                          </h3>
                          <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center">
                            <ShieldCheck className="text-sky-400" size={20} />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Coins size={16} className="mr-2" /> Estimated Cost
                            </span>
                            <span className="text-sky-400 font-semibold text-lg">{formatCurrency(result.mediation.cost)}</span>
                          </div>
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Timer size={16} className="mr-2" /> Duration
                            </span>
                            <span className="text-sky-400 font-semibold">{result.mediation.duration_display}</span>
                          </div>
                          <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                            <span className="text-slate-400 text-sm flex items-center">
                              <TrendingDown size={16} className="mr-2" /> Stress Level
                            </span>
                            <span className="text-sky-400 font-semibold text-sm">{result.mediation.stress_level}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm flex items-center">
                              <Target size={16} className="mr-2" /> Control
                            </span>
                            <span className="text-sky-400 text-sm font-medium">{result.mediation.outcome_control}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-center bg-slate-900/50 border border-slate-800 rounded-2xl p-8"
                >
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Ready to Start Saving?
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                    These figures are estimates based on UK averages. Get a personalised quote for your specific situation.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <Button 
                        data-testid="estimator-cta-contact"
                        className="bg-sky-500 hover:bg-sky-600 text-slate-900 font-semibold btn-hover rounded-full px-8"
                      >
                        Get a Personalised Quote
                      </Button>
                    </Link>
                    <button
                      data-testid="estimator-reset"
                      onClick={handleReset}
                      className="text-slate-400 hover:text-sky-400 transition-colors inline-flex items-center justify-center text-sm px-6 py-3"
                    >
                      <RotateCcw size={14} className="mr-2" />
                      Recalculate
                    </button>
                  </div>
                </motion.div>

                <p className="text-center text-slate-600 text-xs mt-8">
                  * Mediation costs calculated at £1,500 per day (standard mediator rate). Court estimates based on UK Ministry of Justice data and Civil Mediation Council figures. Individual cases may vary.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

// ============================================
// AI Chatbot Widget
// ============================================
const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm Sermo's AI Mediation Assistant. I can answer questions about mediation, UK legal processes, and how our services work. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const suggestedQuestions = [
    "How does mediation work?",
    "How much can I save vs court?",
    "Is mediation confidential?",
    "What if the other party refuses?"
  ];

  const handleSend = async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I'm having trouble responding right now. Please try again or contact Tracey directly at 07768 683593."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold **text**
        const boldFormatted = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Headers ## text
        if (line.startsWith('## ')) {
          return <p key={i} className="font-semibold text-white text-base mt-3 mb-1" dangerouslySetInnerHTML={{ __html: line.slice(3) }} />;
        }
        // Bullet lists - item
        if (line.startsWith('- ')) {
          return <p key={i} className="ml-3 mb-1" dangerouslySetInnerHTML={{ __html: '• ' + line.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />;
        }
        // Horizontal rule
        if (line.trim() === '---') {
          return <hr key={i} className="border-slate-700 my-3" />;
        }
        // Skip empty lines but preserve spacing
        if (!line.trim()) return <div key={i} className="h-1"></div>;
        // Regular text
        return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: boldFormatted }} />;
      });
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            data-testid="chatbot-toggle"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-sky-500 hover:bg-sky-600 shadow-2xl shadow-sky-500/30 z-40 flex items-center justify-center group"
          >
            <Bot className="text-slate-900" size={28} />
            <span className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-950 animate-pulse"></span>
            <span className="absolute right-full mr-4 px-3 py-1.5 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-800">
              Ask our AI Assistant
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed bottom-6 right-6 w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-sky-500/20 to-slate-900 border-b border-slate-800 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center relative">
                  <Bot className="text-slate-900" size={20} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900"></span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Sermo Assistant</p>
                  <p className="text-slate-400 text-xs">Powered by Claude AI</p>
                </div>
              </div>
              <button
                data-testid="chatbot-close"
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                      msg.role === "user"
                        ? "bg-sky-500 text-slate-900 font-medium rounded-br-sm"
                        : "bg-slate-800 text-slate-200 rounded-bl-sm"
                    }`}
                    data-testid={`chat-message-${idx}`}
                  >
                    {msg.role === "user" ? msg.content : <div>{formatMessage(msg.content)}</div>}
                  </div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                      <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                      <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggested Questions */}
            {messages.length === 1 && !loading && (
              <div className="px-4 pb-2">
                <p className="text-slate-500 text-xs mb-2">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      data-testid={`suggested-question-${i}`}
                      onClick={() => handleSend(q)}
                      className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-full px-3 py-1.5 transition-colors border border-slate-700"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex space-x-2">
                <Input
                  data-testid="chatbot-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Ask about mediation..."
                  disabled={loading}
                  className="bg-slate-950 border-slate-800 text-white focus:border-sky-500 flex-1"
                />
                <Button
                  data-testid="chatbot-send"
                  onClick={() => handleSend()}
                  disabled={loading || !input.trim()}
                  className="bg-sky-500 hover:bg-sky-600 text-slate-900 flex-shrink-0"
                >
                  <Send size={16} />
                </Button>
              </div>
              <p className="text-slate-600 text-xs mt-2 text-center">
                AI-powered • For information only, not legal advice
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Smooth Scroll Provider
const SmoothScrollProvider = ({ children }) => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
};

// Main App
function App() {
  return (
    <div className="App min-h-screen bg-slate-950">
      <BrowserRouter>
        <SmoothScrollProvider>
          <Navigation />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/triage" element={<TriagePage />} />
              <Route path="/estimator" element={<CostEstimatorPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Routes>
          </main>
          <Footer />
          <ChatbotWidget />
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: {
                background: '#0f172a',
                border: '1px solid #1e293b',
                color: '#f1f5f9',
              },
            }}
          />
        </SmoothScrollProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
